export const DEFAULT_IDLE_TIMEOUT_MINUTES = 30
export const LAST_ACTIVITY_STORAGE_KEY = 'notch.lastActivity'

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'click', 'scroll', 'touchstart', 'wheel'] as const
const CHECK_INTERVAL_MS = 15_000
const ACTIVITY_THROTTLE_MS = 5_000

export type IdleTimerOptions = {
  /** Idle threshold in milliseconds. Values <= 0 disable the timer. */
  thresholdMs?: number
  /** Override for tests (defaults to window). */
  target?: Window & typeof globalThis
  /** Override for tests (defaults to localStorage). */
  storage?: Storage
}

function resolveThresholdMs(override?: number): number {
  if (typeof override === 'number' && !Number.isNaN(override)) {
    return override
  }
  const raw = import.meta.env.VITE_IDLE_TIMEOUT_MINUTES
  if (raw === undefined || raw === '') {
    return DEFAULT_IDLE_TIMEOUT_MINUTES * 60 * 1000
  }
  const minutes = Number(raw)
  if (Number.isNaN(minutes)) {
    return DEFAULT_IDLE_TIMEOUT_MINUTES * 60 * 1000
  }
  // 0 (or negative) disables the timer — do not coalesce with ||, or the kill switch breaks.
  return minutes * 60 * 1000
}

export class IdleTimer {
  private thresholdMs: number
  private target: Window & typeof globalThis
  private storage: Storage
  private onIdle: (() => void) | null = null
  private intervalId: ReturnType<typeof setInterval> | null = null
  private lastWriteAt = 0
  private hasFiredOrStopped = false
  private boundOnActivity: () => void
  private boundOnVisibilityChange: () => void

  constructor(options: IdleTimerOptions = {}) {
    this.thresholdMs = resolveThresholdMs(options.thresholdMs)
    this.target = options.target ?? window
    this.storage = options.storage ?? localStorage
    this.boundOnActivity = () => this.recordActivity()
    this.boundOnVisibilityChange = () => this.onVisibilityChange()
  }

  get isEnabled(): boolean {
    return this.thresholdMs > 0
  }

  start(onIdle: () => void): void {
    this.stop()
    if (!this.isEnabled) {
      return
    }

    this.onIdle = onIdle
    this.hasFiredOrStopped = false
    this.lastWriteAt = 0

    const last = this.readLastActivity()
    if (last === null || Date.now() - last >= this.thresholdMs) {
      this.writeLastActivity(Date.now())
    }

    for (const eventName of ACTIVITY_EVENTS) {
      this.target.addEventListener(eventName, this.boundOnActivity, { passive: true })
    }
    this.target.document.addEventListener('visibilitychange', this.boundOnVisibilityChange)

    this.intervalId = setInterval(() => this.checkIdle(), CHECK_INTERVAL_MS)
    this.checkIdle()
  }

  stop(): void {
    this.hasFiredOrStopped = true
    this.onIdle = null

    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }

    for (const eventName of ACTIVITY_EVENTS) {
      this.target.removeEventListener(eventName, this.boundOnActivity)
    }
    this.target.document.removeEventListener('visibilitychange', this.boundOnVisibilityChange)
  }

  /** Exposed for tests: force an immediate idle check. */
  checkIdle(): void {
    if (this.hasFiredOrStopped || !this.onIdle || !this.isEnabled) {
      return
    }

    const lastActivity = this.readLastActivity()
    if (lastActivity === null) {
      this.writeLastActivity(Date.now())
      return
    }

    if (Date.now() - lastActivity >= this.thresholdMs) {
      const callback = this.onIdle
      // Mark fired before stop so overlapping checks are no-ops; stop clears listeners/interval.
      this.hasFiredOrStopped = true
      this.onIdle = null
      if (this.intervalId !== null) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
      for (const eventName of ACTIVITY_EVENTS) {
        this.target.removeEventListener(eventName, this.boundOnActivity)
      }
      this.target.document.removeEventListener('visibilitychange', this.boundOnVisibilityChange)
      callback()
    }
  }

  private onVisibilityChange(): void {
    if (this.target.document.visibilityState === 'visible') {
      this.checkIdle()
    }
  }

  private recordActivity(): void {
    if (this.hasFiredOrStopped) {
      return
    }
    const now = Date.now()
    if (now - this.lastWriteAt < ACTIVITY_THROTTLE_MS) {
      return
    }
    this.lastWriteAt = now
    this.writeLastActivity(now)
  }

  private readLastActivity(): number | null {
    const raw = this.storage.getItem(LAST_ACTIVITY_STORAGE_KEY)
    if (raw === null || raw === '') {
      return null
    }
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  }

  private writeLastActivity(timestamp: number): void {
    this.storage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp))
  }
}

export const idleTimer = new IdleTimer()
